import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer,
} from "recharts";
import { TrendPoint } from "../types/PerformanceTypes";
import { METRIC_COLORS, METRIC_LABELS, AXIS } from "../../../constants/constants";
import { shortDate, tickFmt } from "../utils/PerformanceUtils";
import Card from "../ui/Card";
import SectionTitle from "../ui/SectionTitle";
import ChartTooltip from "../ui/ChartTooltip";

const IMP_METRICS = [
  "BUSINESS_IMPRESSIONS_DESKTOP_MAPS",
  "BUSINESS_IMPRESSIONS_DESKTOP_SEARCH",
  "BUSINESS_IMPRESSIONS_MOBILE_MAPS",
  "BUSINESS_IMPRESSIONS_MOBILE_SEARCH",
];

const ACT_METRICS = ["CALL_CLICKS", "WEBSITE_CLICKS", "DIRECTION_REQUESTS"];

// ── Props ────────────────────────────────────────────────────────────────────

interface TrendsTabProps {
  data:                TrendPoint[];
  compareEnabled?:     boolean;
  compareData?:        TrendPoint[];
  primaryPeriodLabel?:  string;
  comparePeriodLabel?:  string;
}

// ── Period legend ─────────────────────────────────────────────────────────────

const PeriodLegend = ({ primaryLabel, compareLabel }: { primaryLabel: string; compareLabel: string }) => (
  <div className="flex items-center gap-3 text-[11px]">
    <span className="flex items-center gap-1.5 text-blue-600 font-semibold">
      <span className="w-5 border-t-2 border-blue-500 inline-block" />
      {primaryLabel}
    </span>
    <span className="flex items-center gap-1.5 text-violet-400 font-semibold">
      <span className="w-5 border-t-2 border-dashed border-violet-400 inline-block" />
      {compareLabel}
    </span>
  </div>
);

// ── Build merged series ───────────────────────────────────────────────────────

const buildMerged = (
  primary: TrendPoint[],
  compare: TrendPoint[] | undefined,
  metrics: string[],
  compareEnabled: boolean | undefined,
) => {
  const pData = primary.map(d => ({ ...d, date: shortDate(String(d.date)) }));
  if (!compareEnabled || !compare) return pData;
  const cData = compare.map(d => ({ ...d, date: shortDate(String(d.date)) }));
  const maxLen = Math.max(pData.length, cData.length);
  return Array.from({ length: maxLen }, (_, i) => ({
    ...(pData[i] || { date: `J+${i}` }),
    ...Object.fromEntries(metrics.map(k => [`cmp_${k}`, (cData[i] as any)?.[k] ?? null])),
  }));
};

// ── Main ─────────────────────────────────────────────────────────────────────

const TrendsTab = ({
  data,
  compareEnabled,
  compareData,
  primaryPeriodLabel  = "Période A",
  comparePeriodLabel  = "Période B",
}: TrendsTabProps) => {
  const impData = buildMerged(data, compareData, IMP_METRICS, compareEnabled);
  const actData = buildMerged(data, compareData, ACT_METRICS, compareEnabled);

  return (
    <div className="flex flex-col gap-5 animate-[gbpFadeIn_0.2s_ease]">

      {/* ── Impressions chart ── */}
      <Card>
        <div className="flex items-center justify-between mb-1">
          <SectionTitle>Impressions — toutes sources</SectionTitle>
          {compareEnabled && (
            <PeriodLegend primaryLabel={primaryPeriodLabel} compareLabel={comparePeriodLabel} />
          )}
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={impData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="date" {...AXIS} />
            <YAxis {...AXIS} tickFormatter={tickFmt} />
            <ChartTooltip />
            <Legend iconType="circle" iconSize={7}
                    wrapperStyle={{ fontSize: 12, color: "#64748b" }} />
            {/* Primary lines */}
            {IMP_METRICS.map((k) => (
              <Line key={k} type="monotone" dataKey={k}
                    name={METRIC_LABELS[k]} stroke={METRIC_COLORS[k]}
                    dot={false} strokeWidth={2} />
            ))}
            {/* Compare lines (dashed) */}
            {compareEnabled && IMP_METRICS.map((k) => (
              <Line key={`cmp_${k}`} type="monotone" dataKey={`cmp_${k}`}
                    stroke={METRIC_COLORS[k]} strokeDasharray="4 3"
                    strokeOpacity={0.45} dot={false} strokeWidth={1.5}
                    legendType="none" name={`${METRIC_LABELS[k]} (B)`} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* ── Actions chart ── */}
      <Card>
        <div className="flex items-center justify-between mb-1">
          <SectionTitle>Actions — toutes catégories</SectionTitle>
          {compareEnabled && (
            <PeriodLegend primaryLabel={primaryPeriodLabel} compareLabel={comparePeriodLabel} />
          )}
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={actData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="date" {...AXIS} />
            <YAxis {...AXIS} tickFormatter={tickFmt} />
            <ChartTooltip />
            <Legend iconType="circle" iconSize={7}
                    wrapperStyle={{ fontSize: 12, color: "#64748b" }} />
            {ACT_METRICS.map((k) => (
              <Line key={k} type="monotone" dataKey={k}
                    name={METRIC_LABELS[k]} stroke={METRIC_COLORS[k]}
                    dot={false} strokeWidth={2} />
            ))}
            {compareEnabled && ACT_METRICS.map((k) => (
              <Line key={`cmp_${k}`} type="monotone" dataKey={`cmp_${k}`}
                    stroke={METRIC_COLORS[k]} strokeDasharray="4 3"
                    strokeOpacity={0.45} dot={false} strokeWidth={1.5}
                    legendType="none" name={`${METRIC_LABELS[k]} (B)`} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Card>

    </div>
  );
};

export default TrendsTab;